import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link2, Link2Off, RefreshCw, HelpCircle, ExternalLink } from "lucide-react";
import DhanConnectModal from "@/components/DhanConnectModal";
import { DHAN_REFERRAL_URL } from "@/components/BuyViaDhan";

/** Persistent DHAN connection strip — the user's broker journey lives here:
 *  connect → see status/balance → update expired token → disconnect. */
export default function BrokerStatusChip() {
  const [status, setStatus] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dhan/status");
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const disconnect = async () => {
    if (!window.confirm("Disconnect your DHAN account from ProTradeIQ? Your stored credentials will be deleted.")) return;
    setBusy(true);
    try {
      await api.post("/dhan/disconnect");
      toast.success("DHAN disconnected");
      load();
    } catch {
      toast.error("Could not disconnect");
    } finally { setBusy(false); }
  };

  if (!status) return null;
  const stale = !status.connected && status.stale_token;
  const tone = status.connected
    ? "border-[#22C55E]/30 bg-[#22C55E]/5"
    : stale ? "border-[#EF4444]/40 bg-[#EF4444]/5" : "border-[#F59E0B]/30 bg-[#F59E0B]/5";

  return (
    <>
      <div className={`border-b border-zinc-900 px-4 md:px-6 py-2 flex flex-wrap items-center gap-x-4 gap-y-2 ${tone}`} data-testid="broker-status-chip">
        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest">
          <span className={`inline-block h-2 w-2 rounded-full ${status.connected ? "bg-[#22C55E]" : stale ? "bg-[#EF4444]" : "bg-[#F59E0B]"}`} />
          {status.connected && (
            <span className="text-[#22C55E]" data-testid="broker-status-connected">
              DHAN connected · {status.client_id}
              {status.available_balance != null && <span className="text-zinc-400"> · Bal ₹{Number(status.available_balance).toLocaleString("en-IN")}</span>}
            </span>
          )}
          {status.connected && status.token_hours_left != null && (
            <span className={`px-1.5 py-0.5 border ${status.token_hours_left <= 1 ? "border-[#F59E0B] text-[#F59E0B] animate-pulse" : "border-zinc-800 text-zinc-500"}`}
                  title={`Token expires ${new Date(status.token_exp_iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST`} data-testid="broker-token-expiry">
              token {status.token_hours_left <= 1 ? `expires in ${Math.max(1, Math.round(status.token_hours_left * 60))} min` : `${Math.floor(status.token_hours_left)}h left`}
            </span>
          )}
          {stale && <span className="text-[#EF4444]" data-testid="broker-status-stale">DHAN token expired · {status.client_id} — update it to keep 1-click execution</span>}
          {!status.connected && !stale && <span className="text-[#F59E0B]" data-testid="broker-status-disconnected">DHAN not connected — connect for 1-click execution</span>}
        </div>

        <div className="flex items-center gap-1.5 ml-auto">
          {status.connected || stale ? (
            <>
              <button
                onClick={() => setModalOpen(true)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 border text-[9px] uppercase tracking-widest font-mono font-bold transition-colors ${stale ? "border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 animate-pulse" : "border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white"}`}
                data-testid="broker-update-token-btn"
              >
                <RefreshCw className="h-3 w-3" /> Update token
              </button>
              <button
                onClick={disconnect} disabled={busy}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-zinc-800 text-zinc-500 hover:border-[#EF4444]/60 hover:text-[#EF4444] text-[9px] uppercase tracking-widest font-mono transition-colors disabled:opacity-50"
                data-testid="broker-disconnect-btn"
              >
                <Link2Off className="h-3 w-3" /> Disconnect
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#22C55E] bg-[#22C55E]/10 text-[#22C55E] hover:bg-[#22C55E]/20 text-[9px] uppercase tracking-widest font-mono font-bold transition-colors"
                data-testid="broker-connect-btn"
              >
                <Link2 className="h-3 w-3" /> Connect DHAN
              </button>
              <a href={DHAN_REFERRAL_URL} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 px-2.5 py-1 border border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-white text-[9px] uppercase tracking-widest font-mono transition-colors"
                 data-testid="broker-open-account-link">
                <ExternalLink className="h-2.5 w-2.5" /> Open account
              </a>
            </>
          )}
          <Link to="/dhan-guide" title="How to connect DHAN — full guide" className="text-zinc-500 hover:text-[#3B9BFF] p-1" data-testid="broker-guide-link">
            <HelpCircle className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      <DhanConnectModal open={modalOpen} onClose={() => setModalOpen(false)} onConnected={() => { setModalOpen(false); load(); }} />
    </>
  );
}
